import type {
  Database,
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  TaskComment,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  Contact,
  EstimatingTeamMember,
  Supplier,
  TermsTemplate,
  RateLibraryItem,
  RICSBenchmark,
  CommercialHealthMetric,
  Employer,
  ClientEmployerRelationship,
  EntitlementAlert,
  ActiveDispute,
  ClientRequest,
  PendingDocumentUpload,
} from '../../../types/database';
import type { Claim, ClaimCorrespondence, ClaimSummaryMetrics, ClaimFilters } from '../../../types/claims';
import type { Document } from '../../../types/documents';
import type {
  Activity,
  ActivityFilter,
  ActivityStats,
  CreatePhoneCallData,
  CreateMeetingData,
  CreateEmailData,
  CreateNoteData,
  CreateCommentData,
} from '../../../types/activity';
import { ProjectFoldersService } from '../../ProjectFoldersService';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';
import { geocodeAddress } from '../../../utils/geocoding';
import type { Constructor } from './mixins';

export const withDocuments = <TBase extends Constructor<any>>(Base: TBase) => class Documents extends Base {
  // DOCUMENT METHODS
  // ============================================================================

  /**
   * Add a document (stores metadata and queues for upload if offline)
   */
  public addDocument(document: Omit<Document, 'id' | 'created_at'>): Document {
    const id = this.generateId();
    const newDocument: Document = {
      ...document,
      id,
      created_at: new Date().toISOString(),
    };
    
    this.db.documents[id] = newDocument;
    this.saveToStorage();
    this.notifyListeners();
    return newDocument;
  }

  /**
   * Add a pending document upload (for offline support)
   */
  public addPendingUpload(upload: Omit<PendingDocumentUpload, 'id' | 'createdAt' | 'attemptCount'>): PendingDocumentUpload {
    const id = this.generateId();
    const pending: PendingDocumentUpload = {
      ...upload,
      id,
      createdAt: new Date().toISOString(),
      attemptCount: 0,
    };
    
    this.db.pendingUploads[id] = pending;
    this.saveToStorage();
    this.notifyListeners();
    return pending;
  }

  /**
   * Get documents for a project
   */
  public getProjectDocuments(projectId: string): Document[] {
    if (!this.db.documents) {
      return [];
    }
    try {
      return (Object.values(this.db.documents) as Document[])
        .filter((doc: Document) => doc && doc.project_id === projectId)
        .sort((a: Document, b: Document) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } catch (error) {
      console.error('Error getting project documents:', error);
      return [];
    }
  }

  /**
   * Get pending uploads for a project
   */
  public getPendingUploads(projectId?: string): PendingDocumentUpload[] {
    if (!this.db.pendingUploads) {
      return [];
    }
    try {
      const uploads = (Object.values(this.db.pendingUploads) as PendingDocumentUpload[]).filter((u: PendingDocumentUpload) => u);
      if (projectId) {
        return uploads.filter((u: PendingDocumentUpload) => u.projectId === projectId);
      }
      return uploads;
    } catch (error) {
      console.error('Error getting pending uploads:', error);
      return [];
    }
  }

  /**
   * Update pending upload status
   */
  public updatePendingUpload(id: string, updates: Partial<PendingDocumentUpload>): void {
    if (this.db.pendingUploads[id]) {
      this.db.pendingUploads[id] = {
        ...this.db.pendingUploads[id],
        ...updates,
      };
      this.saveToStorage();
      this.notifyListeners();
    }
  }

  /**
   * Update document with Google Drive details after upload
   */
  public updateDocumentWithDriveInfo(documentId: string, driveInfo: {
    google_drive_id: string;
    google_drive_folder: string;
  }): void {
    if (this.db.documents[documentId]) {
      this.db.documents[documentId] = {
        ...this.db.documents[documentId],
        ...driveInfo,
      };
      this.saveToStorage();
      this.notifyListeners();
    }
  }

  /**
   * Remove pending upload after successful upload
   */
  public removePendingUpload(id: string): void {
    delete this.db.pendingUploads[id];
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Delete a document
   */
  public deleteDocument(id: string): void {
    delete this.db.documents[id];
    // Also remove any pending upload for this document
    const pending = (Object.values(this.db.pendingUploads) as PendingDocumentUpload[]).find((p: PendingDocumentUpload) => p.documentId === id);
    if (pending) {
      delete this.db.pendingUploads[pending.id];
    }
    this.saveToStorage();
    this.notifyListeners();
  }

  // ============================================================================
};
